import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ForwardedRef,
} from "react";

function assignRef<T>(ref: ForwardedRef<T>, value: T | null) {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
}

export function useAnimatedHeight<T extends HTMLElement = HTMLDivElement>(
  forwardedRef?: ForwardedRef<T>
) {
  const containerRef = useRef<T | null>(null);
  const currentHeightRef = useRef(0);
  const [height, setHeight] = useState(0);

  const mergedRef = useCallback(
    (node: T | null) => {
      containerRef.current = node;
      if (forwardedRef) assignRef(forwardedRef, node);
    },
    [forwardedRef]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let resizeObserver: ResizeObserver;

    const measure = () => {
      const natural = Array.from(container.children).reduce<number>(
        (total, currentChild) => {
          total += currentChild.clientHeight;

          return total;
        },
        0
      );

      if (
        !currentHeightRef.current ||
        (natural && natural !== currentHeightRef.current)
      ) {
        currentHeightRef.current = natural;
        setHeight(natural);
      }
    };

    // Observe children (not the container itself) to avoid a ResizeObserver loop:
    // the container's height transitions would otherwise retrigger the observer.
    const observeChildren = () => {
      resizeObserver = new ResizeObserver(measure);
      Array.from(container.children).forEach((child) =>
        resizeObserver.observe(child)
      );
    };

    // Re-observe when children are added or removed.
    const mutationObserver = new MutationObserver(() => {
      observeChildren();
      measure();
    });
    mutationObserver.observe(container, { childList: true });

    observeChildren();
    measure();

    return () => {
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return {
    ref: forwardedRef ? mergedRef : containerRef,
    style: {
      height,
      overflow: "hidden" as const,
      transition: "height 350ms ease-in-out",
    },
  };
}
